import { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { toast } from 'sonner';
import { Shield, X, Plus, Lock, AlertTriangle, CheckCircle2, KeyRound } from 'lucide-react';

/**
 * Roles & Permissions for one employee.
 *
 * Two distinct flows, each with a styled confirmation dialog (no window.confirm):
 *   1. Assign role → "Review duties → Confirm" dialog showing the role's permission list.
 *   2. Remove role → "Are you sure?" dialog with the impact summary.
 *
 * Both flows refresh the chips after a successful API call. The DELETE endpoint
 * accepts the role's _id (which equals the role code for system roles), so we
 * always pass `role.id` regardless of which role was clicked.
 */
export default function EmployeeRolesSection({ employeeId, employeeName, isPrimaryAdmin }) {
  const { user } = useAuth();
  const canAssign = user?.role === 'admin' || (user?.permissions || []).includes('users.assign_roles');
  const [assigned, setAssigned] = useState([]);
  const [allRoles, setAllRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  // Confirmation dialog state
  const [assignTarget, setAssignTarget] = useState(null);  // role to assign
  const [removeTarget, setRemoveTarget] = useState(null);  // role to remove

  const load = async () => {
    setLoading(true);
    try {
      const [a, all] = await Promise.all([
        api.get(`/users/${employeeId}/roles`),
        canAssign ? api.get('/rbac/roles') : Promise.resolve({ data: [] }),
      ]);
      setAssigned(a.data);
      setAllRoles(all.data);
    } catch (e) { /* not authorised — hide section */ }
    finally { setLoading(false); }
  };
  useEffect(() => { if (employeeId) load(); }, [employeeId]); // eslint-disable-line

  const confirmAssign = async () => {
    if (!assignTarget) return;
    setBusy(true);
    try {
      await api.post(`/users/${employeeId}/roles`, { role_id: assignTarget.id });
      toast.success(`"${assignTarget.name}" added to ${employeeName}`);
      setShowPicker(false);
      setAssignTarget(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Add failed');
    } finally { setBusy(false); }
  };

  const confirmRemove = async () => {
    if (!removeTarget) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/users/${employeeId}/roles/${removeTarget.id}`);
      toast.success(`"${removeTarget.name}" removed`);
      setRemoveTarget(null);
      // Quick optimistic update so the chip disappears even before reload finishes
      setAssigned(prev => prev.filter(r => r.id !== removeTarget.id));
      // Then reload to get the canonical state (incl. recomputed legacy roles)
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally { setBusy(false); }
  };

  if (loading) return null;

  const assignedIds = new Set(assigned.map(r => r.id));
  const available = allRoles.filter(r => !assignedIds.has(r.id));

  const effectivePerms = new Set();
  assigned.forEach(r => (r.permissions || []).forEach(p => effectivePerms.add(p)));

  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="employee-roles-section">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-50 text-[#3D4A8E] flex items-center justify-center">
            <Shield className="w-5 h-5" strokeWidth={1.8} />
          </div>
          <div>
            <h3 className="font-heading text-lg font-semibold text-slate-900">Roles & Permissions</h3>
            <p className="text-xs text-slate-500">
              {assigned.length} role{assigned.length !== 1 ? 's' : ''} · {effectivePerms.size} effective permission{effectivePerms.size !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        {canAssign && available.length > 0 && (
          <Button data-testid="open-role-picker" onClick={() => setShowPicker(v => !v)}
                  className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]" size="sm">
            <Plus className="w-4 h-4 mr-1.5" strokeWidth={2} /> Assign role
          </Button>
        )}
      </div>

      {/* Chips */}
      {assigned.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-4">No roles assigned yet.</div>
      ) : (
        <div className="flex flex-wrap gap-2" data-testid="assigned-roles">
          {assigned.map(r => {
            const protectedRole = r.code === 'admin' && isPrimaryAdmin;
            return (
              <div key={r.id} data-testid={`assigned-role-${r.code || r.id}`}
                   className="inline-flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 text-[#3D4A8E] rounded-full pl-3 pr-1 py-1 text-xs font-semibold">
                {r.system && <Lock className="w-3 h-3 opacity-70" />}
                {r.name}
                <span className="text-[10px] opacity-70">· {(r.permissions || []).length} perms</span>
                {canAssign && !protectedRole && (
                  <button onClick={() => setRemoveTarget(r)} disabled={busy}
                          data-testid={`remove-role-${r.code || r.id}`}
                          title={`Remove "${r.name}" from ${employeeName}`}
                          className="ml-1 w-5 h-5 rounded-full hover:bg-white/80 hover:text-rose-600 flex items-center justify-center transition-colors">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Picker */}
      {showPicker && (
        <div className="mt-4 border-t border-slate-100 pt-4" data-testid="role-picker">
          <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Pick a role to add</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {available.map(r => (
              <button key={r.id} data-testid={`pick-role-${r.code || r.id}`}
                      onClick={() => setAssignTarget(r)} disabled={busy}
                      className="text-left bg-slate-50 hover:bg-indigo-50 hover:border-[#3D4A8E]/40 border border-slate-200 rounded-xl p-3 transition">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-slate-900">{r.name}</span>
                  {r.system && <Lock className="w-3 h-3 text-slate-400" />}
                </div>
                {r.description && <p className="text-[11px] text-slate-500 mt-0.5 line-clamp-2">{r.description}</p>}
                <div className="text-[10px] text-slate-400 mt-1">{(r.permissions || []).length} permissions</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* --- Assign confirmation --- */}
      <Dialog open={!!assignTarget} onOpenChange={(o) => !o && !busy && setAssignTarget(null)}>
        <DialogContent data-testid="confirm-assign-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" strokeWidth={1.8} /> Assign role to {employeeName}?
            </DialogTitle>
            <DialogDescription>
              You&apos;re about to give <b>{assignTarget?.name}</b> access to <b>{employeeName}</b>.
              Review what this role grants before confirming.
            </DialogDescription>
          </DialogHeader>
          {assignTarget && (
            <div className="mt-2 space-y-3">
              <div className="bg-indigo-50/60 border border-indigo-200 rounded-xl p-3">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#3D4A8E]" strokeWidth={1.8} />
                  <h4 className="font-heading font-semibold text-slate-900">{assignTarget.name}</h4>
                  {assignTarget.system && <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">System</span>}
                </div>
                {assignTarget.description && (
                  <p className="text-xs text-slate-600 mt-1.5 leading-relaxed">{assignTarget.description}</p>
                )}
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-1.5 flex items-center gap-1.5">
                  <KeyRound className="w-3 h-3" /> Grants {assignTarget.permissions?.length || 0} permission{(assignTarget.permissions?.length || 0) !== 1 ? 's' : ''}
                </div>
                {assignTarget.permissions?.length === 0 ? (
                  <div className="text-xs text-slate-500 italic py-2">No new permissions (this role grants self-service access only).</div>
                ) : (
                  <div className="max-h-44 overflow-y-auto bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1" data-testid="confirm-assign-perms">
                    {(assignTarget.permissions || []).map(p => (
                      <div key={p} className="flex items-center gap-2 text-xs">
                        <CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0" strokeWidth={2} />
                        <code className="text-slate-700">{p}</code>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)} disabled={busy}
                    className="rounded-full" data-testid="cancel-assign">
              Cancel
            </Button>
            <Button onClick={confirmAssign} disabled={busy} data-testid="confirm-assign"
                    className="rounded-full bg-[#3D4A8E] hover:bg-[#2F3A6E]">
              <CheckCircle2 className="w-4 h-4 mr-1.5" /> {busy ? 'Assigning…' : 'Yes, assign role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* --- Remove confirmation --- */}
      <Dialog open={!!removeTarget} onOpenChange={(o) => !o && !busy && setRemoveTarget(null)}>
        <DialogContent data-testid="confirm-remove-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600" strokeWidth={1.8} /> Remove role?
            </DialogTitle>
            <DialogDescription>
              You&apos;re about to remove <b>{removeTarget?.name}</b> from <b>{employeeName}</b>.
              They will lose any access this role granted on their next page refresh.
            </DialogDescription>
          </DialogHeader>
          {removeTarget && (
            <div className="mt-2 bg-amber-50 border border-amber-200 rounded-xl p-3">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-amber-800 mb-1.5">Will revoke {removeTarget.permissions?.length || 0} permission{(removeTarget.permissions?.length || 0) !== 1 ? 's' : ''}</div>
              {removeTarget.permissions?.length > 0 && (
                <div className="max-h-32 overflow-y-auto space-y-1 mt-2" data-testid="confirm-remove-perms">
                  {(removeTarget.permissions || []).slice(0, 8).map(p => (
                    <div key={p} className="flex items-center gap-2 text-xs text-amber-900">
                      <X className="w-3 h-3 text-amber-700 flex-shrink-0" strokeWidth={2.4} />
                      <code>{p}</code>
                    </div>
                  ))}
                  {removeTarget.permissions.length > 8 && (
                    <div className="text-[10px] text-amber-700 pt-1 italic">…and {removeTarget.permissions.length - 8} more</div>
                  )}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRemoveTarget(null)} disabled={busy}
                    className="rounded-full" data-testid="cancel-remove">
              Cancel
            </Button>
            <Button onClick={confirmRemove} disabled={busy} data-testid="confirm-remove"
                    className="rounded-full bg-rose-600 hover:bg-rose-700 text-white">
              <X className="w-4 h-4 mr-1.5" strokeWidth={2.4} /> {busy ? 'Removing…' : 'Yes, remove role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
